import React, { useState, useEffect, useCallback, useRef } from "react"
import SelectMenu from "@kit/design/select-menu"
import { useCurrentUser } from "hooks/use-current-user"
import Button from "@kit/design/button"
import { switchToUser } from "./switch-user-button"

/**
 * AdminSwitchUser renders the admin impersonation banner and user switcher.
 *
 * When impersonating: shows a colored banner with account info, a "switch to
 * another account" link (which opens an async search), and stale-tab detection.
 *
 * When not impersonating: shows an inline async user search select.
 *
 * All data is read from window.app (via useCurrentUser) — no server-side props needed.
 */
export function AdminSwitchUser() {
  const user = useCurrentUser()
  const impersonating = !user.anonymous && user.account_id != null
  const bannerHue = impersonating ? ((user.account_id * 137) % 320) + 20 : 200
  const bannerColor = `hsl(${bannerHue}, 70%, 40%)`

  const [isStale, setIsStale] = useState(false)
  const [currentColor, setCurrentColor] = useState(bannerColor)
  const [searchOpen, setSearchOpen] = useState(false)

  const checkStaleness = useCallback(() => {
    if (!impersonating) return
    fetch("/switch_user/status", { credentials: "same-origin" })
      .then(response => response.json())
      .then(data => {
        const stale = String(data.impersonating_user_id) !== String(user.id)
        setIsStale(stale)
        setCurrentColor(stale ? "#b91c1c" : bannerColor)
      })
      .catch(() => {})
  }, [impersonating, user.id, bannerColor])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) checkStaleness()
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [checkStaleness])

  const handleSwitchBack = e => {
    e.preventDefault()
    switchToUser(user.id)
  }

  const handleOpenSearch = e => {
    e.preventDefault()
    setSearchOpen(true)
  }

  const title = impersonating
    ? `${user.email} — ${user.account_name} (#${user.account_id})`
    : ""

  const showSelect = !impersonating || searchOpen

  return (
    <div
      className="absolute right-0 top-0 bottom-0 flex items-center pr-4 pl-1 text-white w-[300px] max-lg:hidden"
      style={{ background: impersonating ? currentColor : "transparent" }}
    >
      <div
        className="absolute -left-5 top-0 bottom-0 w-10 -skew-x-[15deg] max-lg:hidden"
        style={{ background: impersonating ? currentColor : "transparent" }}
      />
      {showSelect ? (
        <div
          className="relative z-[51]"
          onBlur={
            impersonating
              ? e => {
                  // Close when focus leaves the switcher entirely.
                  // Delay to allow click on an option to fire onChange first.
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setTimeout(() => setSearchOpen(false), 200)
                  }
                }
              : undefined
          }
        >
          <UserSwitcher
            autoFocus={impersonating} // eslint-disable-line jsx-a11y/no-autofocus -- user-initiated action
          />
        </div>
      ) : (
        <div className="relative overflow-hidden min-w-0 flex-1 text-[13px]">
          <div
            className="overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ textDecoration: isStale ? "line-through" : "" }}
            title={title}
          >
            <strong>{user.account_name}</strong> (
            <a
              href={`/admin/accounts/${user.account_id}`}
              className="text-white hover:text-white underline"
            >
              #{user.account_id}
            </a>
            )
          </div>
          {!isStale && (
            <div className="text-[11px] whitespace-nowrap mt-px h-4 leading-4">
              <Button
                is="plain"
                size="sm"
                onClick={handleOpenSearch}
                className="text-white opacity-85 underline hover:opacity-100 !p-0 !h-auto !text-[11px] !leading-4 !min-h-0"
              >
                Switch to another account
              </Button>
            </div>
          )}
          {isStale && (
            <div className="text-[11px] whitespace-nowrap mt-px h-4 leading-4">
              ⚠️ Switched in another tab.{" "}
              <Button
                is="plain"
                size="sm"
                onClick={handleSwitchBack}
                className="text-white underline hover:opacity-100 !p-0 !h-auto !text-[11px] !leading-4 !min-h-0"
              >
                Switch back
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

async function fetchUsers(input, { signal }) {
  const response = await fetch(
    `/api/v3/users.json?q=${encodeURIComponent(input)}`,
    { credentials: "same-origin", signal }
  )
  const data = await response.json()
  return data.map(user => ({ label: user.email, value: String(user.id) }))
}

function UserSwitcher({ autoFocus }) {
  const ref = useRef(null)

  useEffect(() => {
    if (autoFocus && ref.current) {
      ref.current.focus()
    }
  }, [autoFocus])

  const handleChange = event => {
    if (event.target.value) {
      switchToUser(event.target.value)
    }
  }

  return (
    <div className="w-[250px] text-sm leading-normal">
      <SelectMenu
        ref={ref}
        async
        options={fetchUsers}
        onChange={handleChange}
        placeholder="Switch to another account"
        minCharsToLoad={1}
        className="!bg-white"
        filter={options => options}
      />
    </div>
  )
}
